import axios from 'axios';
import { browser } from '../../../independent/agent/common';
import { ptToCanvas } from '../../../independent/unit';
import ImageQuickLookManager from '../image-toolkit/image-quick-look-manager';
import ImageUrl from '../image-toolkit/image-url';

const DEFAULT_DPI = 72;
const DEFAULT_SCALE_MAC = 1;
const DEFAULT_SCALE_WIN = 0.75;
const DEFAULT_SCALE = browser.isMac ? DEFAULT_SCALE_MAC : DEFAULT_SCALE_WIN;
const ZERO_DPI = 0;

interface IOriginalSize {
    width: number;
    height: number;
}
type CdnUrl = string;
type IImageOriginalMap = Map<CdnUrl, IOriginalSize>;
// 从这个路径下拷贝而来：@src/slide/application/service/copy-paste/cp-image.new/image-edit-manager/core/editor-image
export interface EditorImage {
    width: number;
    height: number;
    imageName: string;
    src: string;
    backupSrc?: string;
    imageType: string;
    prstGeom: {
        prstGeomType: string;
    };
    style?: { position: string; left: number; top: number; width: number; height: number; opacity: number; };
}
type IPromiseResult = Promise<IOriginalSize | undefined>;

/**
 * 提供图片的原始尺寸，通过dpi计算得到
 */
export default class ImageOriginalSizeWithDPI {
    // 图片原始尺寸，使用relId作为key
    private static originalSizes: IImageOriginalMap = new Map();
    // 请求图片原始尺寸接口的map，避免重复请求
    private static requestMap: Map<string, IPromiseResult> = new Map();

    /**
    * 根据cdnUrl获取图片原始尺寸
    * ImageQuickLookManager没有对现有的图片做处理，调用getResourceByUrl返回undefined，仅对新插入图片做处理
    * 该方法仅新插入图片时计算图片的style信息，或者重置图片大小，或者展示图片原始尺寸时需要。加载页面已有图片时不需要
    * @param relId 与 SDK 约定的 ID 格式: 'rel:xxxxxxx'
    * @returns
    */
    public static getImageOriginalSize(relId: string): IOriginalSize | undefined {
        const imageQuickLockInstance = ImageQuickLookManager.getInstance();
        const id = imageQuickLockInstance.getResourceByUrl(relId)?.id ?? relId;
        return this.originalSizes.get(id);
    }

    public static getAndUpdateImageOriginalSize(
        relId: string, url: string,
        callback?: (size: IOriginalSize) => void,
    ): IOriginalSize | undefined {
        const originalSize = this.getImageOriginalSize(relId);
        if (originalSize) return originalSize;
        this.updateImageOriginalSizeMap(url, relId).then((originalSize) => {
            if (originalSize) {
                this.originalSizes.set(relId, originalSize);
                callback?.(originalSize);
            }
        });
    }

    /**
    * 更新存储图片原始尺寸的map
    */
    public static async updateImageOriginalSizeMap(url: string, relId: string): IPromiseResult {
        // 如果有缓存则return
        const originalSize = this.getImageOriginalSize(relId);
        if (originalSize) return Promise.resolve(originalSize);
        // 如果未缓存，则更新缓存
        const cdnUrl = this.getCdnUrl(relId, url);
        return cdnUrl ? this.fetchAndUpdateImageSize(cdnUrl, relId) : Promise.resolve(undefined);
    }

    /**
     * 更新粘贴图片或者插入图片时的图片原始尺寸缓存
     */
    public static updateImageOriginalSizeByLocalImages(images: EditorImage[]) {
        images.forEach((image) => {
            if (!this.getImageOriginalSize(image.src)) {
                this.addOriginalSize(image.src, { width: image.width, height: image.height });
            }
        });
    }

    /**
     * 获取cdn url
     */
    private static getCdnUrl(cdnUrl: string, relId: string): string {
        if (ImageUrl.isCDNUrl(cdnUrl)) return cdnUrl;
        let url = relId;
        const imageQuickLockInstance = ImageQuickLookManager.getInstance();
        // url可能是与SDK 约定的 ID 格式，如rel: xxxxxxx
        if (ImageUrl.isRelationshipUrl(relId)) {
            url = imageQuickLockInstance.getNextUIDataFromSDKSrc(relId).src ?? relId;
        }
        const imageResourceUrl = imageQuickLockInstance.getResourceByUrl(url);
        return ImageUrl.isCDNUrl(url) ? url : (imageResourceUrl?.getCdnUrl() ?? url);
    }

    private static async fetchAndUpdateImageSize(cdnUrl: string, relId: string): IPromiseResult {
        if (!ImageUrl.isCDNUrl(cdnUrl)) return Promise.resolve(undefined);
        const [pureUrl] = cdnUrl.split('?');
        const promise = this.requestMap.get(pureUrl) || this.requestImageInfo(pureUrl, cdnUrl, relId);
        this.requestMap.set(pureUrl, promise);
        return promise;
    }

    private static async requestImageInfo(pureUrl: string, cdnUrl: string, relId: string): IPromiseResult {
        const response = await axios({
            method: 'get',
            url: `${pureUrl}?imageInfo`,
            timeout: 2000,
        });
        let dpi;
        if (response.status === 200) {
            dpi = parseFloat(response.data.vertical_dpi);
        }
        this.addOriginalSize(relId, ImageUrl.getImageSizeFromUrl(cdnUrl), dpi);
        return Promise.resolve(this.getImageOriginalSize(relId));
    }

    private static addOriginalSize(relId: string, size: IOriginalSize, verticalDpi = DEFAULT_DPI): void {
        const imageQuickLockInstance = ImageQuickLookManager.getInstance();
        const resource = imageQuickLockInstance.getResourceByUrl(relId);
        const id = resource?.id ?? relId;
        const dpi = resource?.getVerticalDpi() ?? verticalDpi;
        let scale = DEFAULT_SCALE;
        if (dpi !== ZERO_DPI && !isNaN(DEFAULT_DPI / dpi)) {
            scale = DEFAULT_DPI / dpi;
        }
        this.originalSizes.set(id, {
            width: ptToCanvas(size.width * scale),
            height: ptToCanvas(size.height * scale),
        });
    }
}
